import React from "react";
import styled, { css } from "styled-components";
import { tokens } from "../../theme/tokens";

// Form primitives: FormField wraps a label + control + hint/error.
//
//   <FormField label="Email" hint="We never share it" error={err}>
//       <TextInput value={email} onChange={...} placeholder="you@company.com" />
//   </FormField>
//
// TextInput / TextArea / Select are branded controls. Toggle & Checkbox are
// self-contained (label built in). All presentation-only.

const { colors, radii, spacing, typography, shadows, motion } = tokens;

const controlBase = css`
    width: 100%;
    font-family: ${typography.fonts.body};
    font-size: ${typography.size.base};
    color: ${colors.body};
    background: ${colors.surface};
    border: 1.5px solid ${(p) => (p.$invalid ? colors.status.danger.border : colors.borderStrong)};
    border-radius: ${radii.md};
    transition: border-color ${motion.fast} ${motion.ease},
        box-shadow ${motion.fast} ${motion.ease}, background ${motion.fast} ${motion.ease};
    appearance: none;

    &::placeholder { color: ${colors.faint}; }
    &:hover:not(:disabled):not(:focus) { border-color: ${colors.primary}; }
    &:focus {
        outline: none;
        border-color: ${(p) => (p.$invalid ? colors.status.danger.fg : colors.primary)};
        box-shadow: ${shadows.focus};
        background: ${colors.surface};
    }
    &:disabled { background: ${colors.surfaceSunken}; color: ${colors.muted}; cursor: not-allowed; }
`;

export const TextInput = styled.input`
    ${controlBase};
    height: ${(p) => (p.$size === "lg" ? "50px" : p.$size === "sm" ? "36px" : "44px")};
    padding: 0 14px;
`;

export const TextArea = styled.textarea`
    ${controlBase};
    min-height: 104px;
    padding: 12px 14px;
    line-height: ${typography.leading.normal};
    resize: vertical;
`;

const SelectWrap = styled.div`
    position: relative;
    display: block;
    &::after {
        content: "";
        position: absolute;
        right: 15px;
        top: 50%;
        width: 9px;
        height: 9px;
        border-right: 2px solid ${colors.muted};
        border-bottom: 2px solid ${colors.muted};
        transform: translateY(-65%) rotate(45deg);
        pointer-events: none;
    }
`;

const StyledSelect = styled.select`
    ${controlBase};
    height: ${(p) => (p.$size === "lg" ? "50px" : p.$size === "sm" ? "36px" : "44px")};
    padding: 0 38px 0 14px;
    cursor: pointer;
`;

export const Select = React.forwardRef(function Select({ children, ...rest }, ref) {
    return (
        <SelectWrap>
            <StyledSelect ref={ref} {...rest}>
                {children}
            </StyledSelect>
        </SelectWrap>
    );
});

// --- Field wrapper -----------------------------------------------------------
const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-bottom: ${(p) => (p.$flush ? "0" : spacing[4])};
`;

export const Label = styled.label`
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.semibold};
    color: ${colors.heading};
    letter-spacing: 0.01em;
    span.req { color: ${colors.primary}; margin-left: 3px; }
`;

const Hint = styled.span`
    font-size: ${typography.size.xs};
    color: ${(p) => (p.$error ? colors.status.danger.fg : colors.muted)};
    line-height: ${typography.leading.snug};
`;

export function FormField({ label, required, hint, error, htmlFor, flush, children }) {
    return (
        <Field $flush={flush}>
            {label && (
                <Label htmlFor={htmlFor}>
                    {label}
                    {required && <span className="req">*</span>}
                </Label>
            )}
            {children}
            {(error || hint) && <Hint $error={!!error}>{error || hint}</Hint>}
        </Field>
    );
}

// --- Toggle (switch) ---------------------------------------------------------
const ToggleRoot = styled.label`
    display: inline-flex;
    align-items: center;
    gap: ${spacing[3]};
    cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
    opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
    font-size: ${typography.size.base};
    color: ${colors.body};
    user-select: none;
`;

const Track = styled.span`
    position: relative;
    width: 44px;
    height: 26px;
    border-radius: ${radii.pill};
    background: ${(p) => (p.$on ? colors.primary : colors.borderStrong)};
    transition: background ${motion.base} ${motion.ease};
    flex-shrink: 0;
    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #fff;
        box-shadow: ${shadows.xs};
        transition: transform ${motion.base} ${motion.ease};
        transform: translateX(${(p) => (p.$on ? "18px" : "0")});
    }
`;

const HiddenInput = styled.input`
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    &:focus-visible + span { box-shadow: ${shadows.focus}; }
`;

export function Toggle({ checked, onChange, disabled, label, ...rest }) {
    return (
        <ToggleRoot $disabled={disabled}>
            <HiddenInput
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                {...rest}
            />
            <Track $on={checked} />
            {label}
        </ToggleRoot>
    );
}

// --- Checkbox ----------------------------------------------------------------
const CheckRoot = styled.label`
    display: inline-flex;
    align-items: center;
    gap: ${spacing[2]};
    cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
    font-size: ${typography.size.base};
    color: ${colors.body};
    user-select: none;
`;

const Box = styled.span`
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1.5px solid ${(p) => (p.$on ? colors.primary : colors.borderStrong)};
    background: ${(p) => (p.$on ? colors.primary : colors.surface)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all ${motion.fast} ${motion.ease};
    flex-shrink: 0;
    svg { opacity: ${(p) => (p.$on ? 1 : 0)}; }
`;

export function Checkbox({ checked, onChange, disabled, label, ...rest }) {
    return (
        <CheckRoot $disabled={disabled}>
            <HiddenInput
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                {...rest}
            />
            <Box $on={checked}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                        d="M2.5 6.2 4.7 8.4 9.5 3.6"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>
            {label}
        </CheckRoot>
    );
}

export default FormField;
